import type { SegmentKind } from '@/domains/pages/ai-proficiency/utils/segment-api-payloads';

export const NO_SEGMENTS_EMPTY_REASON_PREFIX = 'no_segments:';

/** Preferred method: send team / market / tenure on interactions via the Interaction API. */
export const SEGMENT_DIMENSION_DOCS_URL =
  'https://docs.nebuly.com/tracking/api-reference/events/post-events-interaction-with-trace-v2';

export function buildNoSegmentsEmptyReason(kind: SegmentKind): string {
  return `${NO_SEGMENTS_EMPTY_REASON_PREFIX}${kind}`;
}

export function parseNoSegmentsEmptyReason(reason?: string): SegmentKind | null {
  if (!reason?.startsWith(NO_SEGMENTS_EMPTY_REASON_PREFIX)) return null;
  const kind = reason.slice(NO_SEGMENTS_EMPTY_REASON_PREFIX.length);
  if (kind === 'team' || kind === 'market' || kind === 'tenure') return kind;
  return null;
}

type SegmentEmptyCopy = {
  title: string;
  description: string;
  docsCtaLabel: string;
  mappingCtaLabel: string;
  mappingCtaTooltip: string;
  mappingDialogTitle: string;
  mappingDialogDescription: string;
  mappingNoTagsMessage: string;
};

const SEGMENT_EMPTY_COPY: Record<SegmentKind, SegmentEmptyCopy> = {
  team: {
    title: 'No teams configured',
    description:
      "Nebuly hasn't received team data for your users yet. Once teams are sent through the Interaction API, this report will show proficiency scores, ladder position, and level movement by team.",
    docsCtaLabel: 'How to send team values',
    mappingCtaLabel: 'Map to tags',
    mappingCtaTooltip:
      'Backward compatibility only. Maps an existing interaction tag to team segments so historical data can be backfilled. This does not replace sending team on each interaction via the Interaction API — use that for all new data.',
    mappingDialogTitle: 'Map a tag to teams',
    mappingDialogDescription:
      'Choose which user tag should be used to populate team segments from existing interaction data.',
    mappingNoTagsMessage:
      'No interaction tags were found for this project. Tags must be present on historical interactions before they can be mapped.',
  },
  market: {
    title: 'No markets configured',
    description:
      "Nebuly hasn't received market data for your users yet. Once markets are sent through the Interaction API, this report will show proficiency scores, ladder position, and level movement by market.",
    docsCtaLabel: 'How to send market values',
    mappingCtaLabel: 'Map to tags',
    mappingCtaTooltip:
      'Backward compatibility only. Maps an existing interaction tag to market segments so historical data can be backfilled. This does not replace sending market on each interaction via the Interaction API — use that for all new data.',
    mappingDialogTitle: 'Map a tag to markets',
    mappingDialogDescription:
      'Choose which user tag should be used to populate market segments from existing interaction data.',
    mappingNoTagsMessage:
      'No interaction tags were found for this project. Tags must be present on historical interactions before they can be mapped.',
  },
  tenure: {
    title: 'No tenure bands configured',
    description:
      "Nebuly hasn't received tenure band data for your users yet. Once tenure bands are sent through the Interaction API, this report will show proficiency scores, ladder position, and level movement by tenure band.",
    docsCtaLabel: 'How to send tenure values',
    mappingCtaLabel: 'Map to tags',
    mappingCtaTooltip:
      'Backward compatibility only. Maps an existing interaction tag to tenure band segments so historical data can be backfilled. This does not replace sending tenure on each interaction via the Interaction API — use that for all new data.',
    mappingDialogTitle: 'Map a tag to tenure bands',
    mappingDialogDescription:
      'Choose which user tag should be used to populate tenure band segments from existing interaction data.',
    mappingNoTagsMessage:
      'No interaction tags were found for this project. Tags must be present on historical interactions before they can be mapped.',
  },
};

export function getSegmentEmptyCopy(kind: SegmentKind): SegmentEmptyCopy {
  return SEGMENT_EMPTY_COPY[kind];
}

export type SegmentEmptyReport = 'proficiency' | 'spend';

const SEGMENT_EMPTY_SPEND_TITLE = 'No data available';

const SEGMENT_EMPTY_SPEND_DESCRIPTION: Record<SegmentKind, string> = {
  team: "Nebuly hasn't received team data for your users in the selected timeframe. Once teams are sent through the Interaction API, this report will show AI spend, cost per task, and spend trends by team.",
  market:
    "Nebuly hasn't received market data for your users in the selected timeframe. Once markets are sent through the Interaction API, this report will show AI spend, cost per task, and spend trends by market.",
  tenure:
    "Nebuly hasn't received tenure band data for your users in the selected timeframe. Once tenure bands are sent through the Interaction API, this report will show AI spend, cost per task, and spend trends by tenure band.",
};

export function getSegmentEmptyTitle(kind: SegmentKind, report: SegmentEmptyReport): string {
  if (report === 'spend') return SEGMENT_EMPTY_SPEND_TITLE;
  return SEGMENT_EMPTY_COPY[kind].title;
}

export function getSegmentEmptyDescription(
  kind: SegmentKind,
  report: SegmentEmptyReport,
): string {
  if (report === 'spend') return SEGMENT_EMPTY_SPEND_DESCRIPTION[kind];
  return SEGMENT_EMPTY_COPY[kind].description;
}
